/**
 * @file SitePreviewFrame.tsx
 * @description Предпросмотр сайта в кабинете (phase19.16 Р1; SB51;
 * uiux-design-r1 §1 «живой предпросмотр справа», §10 `SitePreviewFrame`).
 *
 * Настоящая витрина в iframe в режиме предпросмотра черновика (токен по
 * контракту §7) или опубликованного вида («Сейчас на сайте»), на выбранном
 * экране и ширине; 768 и 1280 уменьшаются под колонку.
 *
 * Своего состояния у рамки нет: экран, ширина, режим и якорь — в общем
 * состоянии предпросмотра каркаса (`view.preview`), оформление — в общей
 * части THEME (`view.parts.theme`). Поэтому панели не передают рамке
 * ничего, а будущая оболочка «Сайт → Конструктор» (фаза §7.14) держит
 * одну рамку на все панели. Несохранённое оформление и прокрутка к шапке
 * или подвалу уходят в iframe сообщениями, клик по шапке или подвалу на
 * канве выбирает область (Р1-6а); после сохранения любой части витрина
 * перерисовывается.
 */

import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { useCanvasFontRegistry } from "../shop-fonts-context";
import { useSiteView } from "../site-view-context";
import { PreviewToolbar } from "./PreviewToolbar";
import { useScaledFrame } from "./useScaledFrame";
import { useSitePreviewToken } from "../../../hooks/site-view/useSitePreviewToken";
import { draftFrameUrl, liveFrameUrl, plainPreviewUrl, PREVIEW_SCREENS, PREVIEW_WIDTHS, screenPath } from "../../../lib/site-view/preview";
import type { SitePreviewSource, SiteViewDraft } from "../../../lib/site-view/types";
import { cn } from "@/lib/utils";

interface Props {
  /** Версия из истории вместо черновика («Посмотреть»). */
  source?: SitePreviewSource;
  onFullscreen?: () => void;
  /** Высота области: колонка экрана. */
  heightClass?: string;
  /** Занять оставшуюся высоту родителя-колонки (окно на весь экран). */
  fill?: boolean;
}

/** Версии всех частей: новая версия любой части — перерисовать витрину. */
function draftVersionKey(draft: SiteViewDraft | null): string {
  return draft ? Object.values(draft.parts).map((p) => `${p.part}:${p.version}`).join("|") : "";
}

export function SitePreviewFrame({ source, onFullscreen, heightClass = "h-[calc(100vh-14rem)]", fill = false }: Props) {
  const view = useSiteView();
  const { siteUrl, preview: state } = view;
  const mode = source ? "draft" : state.mode;
  const preview = useSitePreviewToken(mode === "draft" ? source ?? { source: "draft" } : null);
  const frame = useScaledFrame(state.width, {
    theme: source ? undefined : view.parts?.theme.theme,
    fontRegistry: useCanvasFontRegistry(),
    area: state.area,
    onAreaSelect: source ? undefined : state.pickArea,
  });

  const screenDef = PREVIEW_SCREENS.find((s) => s.key === state.screen) ?? PREVIEW_SCREENS[0]!;
  const list = state.screen === "product" ? state.targets.products : state.screen === "page" ? state.targets.pages : null;
  // «Товар» без выбора — первый товар в наличии.
  const slug = state.target ?? list?.[0]?.slug ?? null;
  // Шапка и подвал — сообщением `scroll-to` (без перезагрузки), не якорем в адресе.
  const path = screenPath(screenDef, slug);

  const src = useMemo(() => {
    // Без рамок и ярлыков канвы — это предпросмотр, а не конструктор.
    if (mode === "live") return siteUrl ? plainPreviewUrl(liveFrameUrl(siteUrl, path)) : null;
    return preview.token ? plainPreviewUrl(draftFrameUrl(preview.token.previewUrl, path)) : null;
  }, [mode, siteUrl, path, preview.token]);

  // Новая версия черновика — перерисовать витрину (с задержкой: серия правок).
  const versionKey = source ? "" : draftVersionKey(view.draft);
  const [frameKey, setFrameKey] = useState(0);
  useEffect(() => {
    if (!versionKey) return;
    const t = setTimeout(() => setFrameKey((k) => k + 1), 1200);
    return () => clearTimeout(t);
  }, [versionKey]);

  return (
    <div className={cn("flex min-w-0 flex-col gap-2", fill && "min-h-0 flex-1")}>
      <PreviewToolbar
        screen={state.screen}
        onScreen={state.setScreen}
        targets={list}
        target={slug}
        onTarget={state.setTarget}
        widths={PREVIEW_WIDTHS}
        width={state.width}
        onWidth={state.setWidth}
        mode={mode}
        onMode={source ? null : state.setMode}
        onFullscreen={onFullscreen}
      />
      <div ref={frame.containerRef} className={cn("relative min-w-0 overflow-hidden rounded-xl bg-surface-low", fill ? "min-h-0 flex-1" : heightClass)}>
        {mode === "draft" && preview.error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="type-body max-w-[46ch] text-on-surface-variant">{preview.error}</p>
            <Button variant="outline" onClick={preview.retry}>
              <RefreshCw aria-hidden />
              Повторить
            </Button>
          </div>
        ) : src ? (
          <iframe
            key={`${frameKey}:${mode}`}
            ref={frame.iframeRef}
            src={src}
            title={mode === "draft" ? "Предпросмотр черновика сайта" : "Сайт сейчас"}
            onLoad={frame.onLoad}
            className="absolute left-1/2 top-0 origin-top border-0 bg-white"
            style={frame.style}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6" aria-busy="true">
            <Skeleton className="h-8 w-2/3 rounded-lg" />
            <p className="type-caption text-on-surface-variant">
              {mode === "live" && !siteUrl ? "У магазина пока нет адреса сайта." : "Готовим предпросмотр…"}
            </p>
          </div>
        )}
      </div>
      {screenDef.sample ? (
        <p className="type-caption text-on-surface-variant">
          На экранах корзины, заказа и кабинета — образец данных, настоящих заказов нет.
        </p>
      ) : null}
    </div>
  );
}
